import { useEffect, useState, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faSearch,
  faSortUp,
  faSortDown,
  faSort,
  faPlay,
  faDownLong,
  faDownload,
} from "@fortawesome/free-solid-svg-icons";
import DatePicker from "react-datepicker";
import axios from "axios";
import { API_URL } from "../../config.js";
import moment from "moment";
import { Link, useNavigate } from "react-router-dom";
import { showToast } from "../../components/toast.jsx";
import "./../subscribe.css";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import AudioPlayerPopup from "../../components/AudioPlayerPopup.jsx";

const IncomingCallReport = () => {
  const callTypeOption = [
    {
      value: 0,
      label: "outgoing",
    },
    {
      value: 1,
      label: "incoming",
    },
  ];
  const token = localStorage.getItem("authToken");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [popupAudio, setPopupAudio] = useState(null);

  const [nameFilter, setNameFilter] = useState("");
  const [callFromFilter, setCallFromFilter] = useState("");
  const [callToFilter, setCallToFilter] = useState("");
  const [callTypeFilter, setCallTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assignedToFilter, setAssignedToFilter] = useState("");
  const [createdDateFilter, setCreatedDateFilter] = useState([null, null]);

  const [page, setPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true); // Flag to check if more data is available
  const [sortField, setSortField] = useState(""); // example: 'created_on'
  const [sortOrder, setSortOrder] = useState("asc"); // 'asc' or 'desc'
  const [dropdownOptions, setDropdownOptions] = useState({
    statusList: [],
    assignedToList: [],
  });

  const [totalCount, setTotalCount] = useState(0);
  const [filteredCount, setFilteredCount] = useState(0);
  const navigate = useNavigate();

  const sortBox = (field) => {
    if (sortField === field) {
      const newOrder = sortOrder === "asc" ? "desc" : "asc";
      setSortOrder(newOrder);
      fetchUsers(1); // refetch data on toggle
    } else {
      setSortField(field);
      setSortOrder("asc");
      fetchUsers(1); // refetch data on new field
    }
  };

  const getSortIcon = (field) => {
    if (sortField === field) {
      return sortOrder === "asc" ? faSortUp : faSortDown;
    }
    return faSort; // default icon for inactive columns
  };

  const limit = 100;

  const fetchUsers = async (pageNumber = 1) => {
    try {
      setTableLoading(true);

      const filterParams = {};

      if (createdDateFilter[0] && createdDateFilter[1])
        filterParams.screated_on = moment(createdDateFilter[0]).format(
          "YYYY-MM-DD"
        );
      if (createdDateFilter[0] && createdDateFilter[1])
        filterParams.ecreated_on = moment(createdDateFilter[1]).format(
          "YYYY-MM-DD"
        );
      if (nameFilter) filterParams.fullname = nameFilter;
      if (callFromFilter) filterParams.call_from = callFromFilter;
      if (callToFilter) filterParams.call_to = callToFilter;
      if (callTypeFilter) filterParams.call_type = callTypeFilter;
      if (statusFilter) filterParams.status = statusFilter;
      if (assignedToFilter) filterParams.assignedto = assignedToFilter;

      const response = await axios.get(`${API_URL}incoming-call-summary`, {
        params: {
          ...filterParams,
          page: pageNumber,
          limit,
          orderbycol: sortField,
          orderby: sortOrder,
        },
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setUsers(response.data.data.data);
      setTotalCount(response.data.data.total);
      setHasMoreData(Math.ceil(totalCount / limit));
    } catch (error) {
      setUsers([]);
      setTotalCount(0);
      setError(error.message);
    } finally {
      setTableLoading(false);
    }
  };

  const fetchDropdownOptions = async () => {
    try {
      // setLoading(true);
      const response = await axios.get(
        `${API_URL}incoming-call-summary/status-options`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.status === 200 && response.data) {
        setDropdownOptions((prev) => ({
          ...prev,
          statusList: response.data || [],
        }));
      }
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    }
  };

  const fetchAssignedDropdown = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API_URL}incoming-call-summary/incoming-call/userlist`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setDropdownOptions((prev) => ({
          ...prev,
          assignedToList: response.data.data || [],
        }));
      }
    } catch (error) {
      console.error("Error fetching dropdown options:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers(page);
  }, [
    page,
    callTypeFilter,
    statusFilter,
    assignedToFilter,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    fetchDropdownOptions();
    fetchAssignedDropdown();
  }, []);

  const handleExportData = async () => {
    try {
      setLoading(true);

      const filterParams = {};

      if (createdDateFilter[0] && createdDateFilter[1])
        filterParams.screated_on = moment(createdDateFilter[0]).format(
          "YYYY-MM-DD"
        );
      if (createdDateFilter[0] && createdDateFilter[1])
        filterParams.ecreated_on = moment(createdDateFilter[1]).format(
          "YYYY-MM-DD"
        );
      if (nameFilter) filterParams.fullname = nameFilter;
      if (callFromFilter) filterParams.call_from = callFromFilter;
      if (callToFilter) filterParams.call_to = callToFilter;
      if (callTypeFilter) filterParams.call_type = callTypeFilter;
      if (statusFilter) filterParams.status = statusFilter;
      if (assignedToFilter) filterParams.assignedto = assignedToFilter;

      const response = await axios.get(
        `${API_URL}incoming-call-summary/call-summary/export`,
        {
          params: {
            ...filterParams,
            orderbycol: sortField,
            orderby: sortOrder,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          responseType: "blob",
        }
      );

      if (response.status == 200) {
        const now = new Date();
        const formattedDate = now
          .toLocaleString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
          })
          .replace(/[/, ]+/g, "_"); // Replace separators with underscores

        const fileName = `IncomingCallReport-${formattedDate}.xlsx`;

        // ✅ Create a downloadable link
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatedDateChange = (dates) => setCreatedDateFilter(dates);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= hasMoreData) {
      setPage(newPage);
    }
  };

  const start = (page - 1) * limit + 1;
  const end = Math.min(page * limit, totalCount);

  return (
    <div className="page-header-fixed page-sidebar-fixed mt-5">
      {/* {loading && <LoadingSpinner />} */}
      <div className="panel  panel-default with-rounded-corner with-shadow">
        <div className="d-flex align-items-center justify-content-between">
          <div className="panel-heading border-0">
            <h4 className="panel-title">Manage Call Summary</h4>
          </div>
          <div className="me-3 d-flex align-items-center gap-2">
            <div>
              <span style={{ fontSize: "0.8rem" }} className="me-2">
                {start} to {end} of {totalCount}
              </span>
              <button
                className={`btn btn-default btn-sm ${page === 1 ? "d-none" : ""
                  }`}
                title="Previous"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className={`btn btn-default btn-sm ${page === hasMoreData ? "d-none" : ""
                  }`}
                title="Next"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === hasMoreData}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
            <button
              type="button"
              className="btn btn-default btn-sm pull-right ml-1"
              onClick={handleExportData}
            >
              <i className="ti ti-export"></i>
              {"  "}Export Report
            </button>
          </div>
        </div>

        <div className="table-responsive">
          <table className="table m-b-0">
            <thead>
              <tr>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => sortBox("name")}
                >
                  Name <FontAwesomeIcon icon={getSortIcon("name")} />
                </th>
                <th>From</th>
                <th>To</th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => sortBox("call_type")}
                >
                  Call Type <FontAwesomeIcon icon={getSortIcon("call_type")} />
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => sortBox("status")}
                >
                  Status <FontAwesomeIcon icon={getSortIcon("status")} />
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => sortBox("assigned_to")}
                >
                  Assigned To{" "}
                  <FontAwesomeIcon icon={getSortIcon("assigned_to")} />
                </th>
                <th
                  style={{ cursor: "pointer" }}
                  onClick={() => sortBox("created_on")}
                >
                  Created Date{" "}
                  <FontAwesomeIcon icon={getSortIcon("created_on")} />
                </th>
                <th>Duration</th>
                <th>Start Time</th>
                <th>End Time</th>
                <th>Recording</th>
              </tr>
              <tr>
                <th>
                  <input
                    type="text"
                    placeholder=""
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    style={{
                      width: "90px",
                      padding: "5px",
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") fetchUsers();
                    }}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder=""
                    value={callFromFilter}
                    onChange={(e) => setCallFromFilter(e.target.value)}
                    style={{
                      width: "90px",
                      padding: "5px",
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") fetchUsers();
                    }}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder=""
                    value={callToFilter}
                    onChange={(e) => setCallToFilter(e.target.value)}
                    style={{
                      width: "90px",
                      padding: "5px",
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") fetchUsers();
                    }}
                  />
                </th>
                <th>
                  <select
                    value={callTypeFilter}
                    onChange={(e) => setCallTypeFilter(e.target.value)}
                    style={{ width: "110px", padding: "5px" }}
                  >
                    <option value="">- Select -</option>
                    {callTypeOption &&
                      callTypeOption.map((item, index) => (
                        <option key={index} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                  </select>
                </th>
                <th>
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    style={{ width: "110px", padding: "5px" }}
                  >
                    {dropdownOptions.statusList &&
                      dropdownOptions.statusList.map((item, index) => (
                        <option key={index} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                  </select>
                </th>
                <th>
                  <select
                    value={assignedToFilter}
                    onChange={(e) => setAssignedToFilter(e.target.value)}
                    style={{ width: "110px", padding: "5px" }}
                  >
                    <option value="">- Select -</option>
                    {dropdownOptions.assignedToList &&
                      dropdownOptions.assignedToList.map((item, index) => (
                        <option key={index} value={item.user_id}>
                          {item.user_user_fullname}
                        </option>
                      ))}
                  </select>
                </th>
                <th>
                  <div className="d-flex align-items-center gap-1">
                    <DatePicker
                      selected={createdDateFilter[0]}
                      onChange={handleCreatedDateChange}
                      startDate={createdDateFilter[0]}
                      endDate={createdDateFilter[1]}
                      selectsRange
                      isClearable
                      toggleCalendarOnIconClick
                      placeholderText=""
                      monthsShown={3}
                    />
                    <button
                      type="button"
                      className="btn btn-default btn-xs btn-group-xs"
                      onClick={() => fetchUsers()}
                    >
                      <FontAwesomeIcon icon={faSearch} />
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {tableLoading ? (
                Array.from({ length: 8 }).map((_, index) => (
                  <tr key={index}>
                    {Array.from({ length: 11 }).map((__, i) => (
                      <td key={i}>
                        <div className="skeleton-box"></div>
                      </td>
                    ))}
                  </tr>
                ))
              ) : users.length > 0 ? (
                users.map((user, index) => {
                  return (
                    <tr key={user.id}>
                      <td>
                        <Link
                          to={`/Subscriber/edit/${user.subscriberid}`}
                          className="text-primary"
                        >
                          {user.subscriberfullname}
                        </Link>
                      </td>
                      <td>{user.call_from}</td>
                      <td>{user.call_to}</td>
                      <td>
                        {user.call_type === "0" ? "outgoing" : "incoming"}
                      </td>
                      <td>{user.status}</td>
                      <td>{user.assigned_user}</td>
                      <td>
                        {user.created_on
                          ? moment(user.created_on).format("DD-MM-YYYY hh:mm A")
                          : ""}
                      </td>
                      <td>
                        {user.duration
                          ? `${Math.floor(user.duration / 60)
                            .toString()
                            .padStart(2, "0")}:${(user.duration % 60)
                              .toString()
                              .padStart(2, "0")}`
                          : "00:00"}
                      </td>
                      <td>
                        {user.start_time
                          ? moment(user.start_time).format("hh:mm:ss")
                          : ""}
                      </td>
                      <td>
                        {user.end_time
                          ? moment(user.end_time).format("hh:mm:ss")
                          : ""}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        {(user.s3_recording_url || (user.recording_url && user.recording_url.startsWith("http") && user.recording_url !== "Array")) ? (
                          (() => {
                            const streamUrl = user.s3_recording_url || `${API_URL}incoming-call-summary/recording/${user.auto_id}?token=${token}`;
                            const downloadUrl = `${API_URL}incoming-call-summary/recording/${user.auto_id}?token=${token}&download=1`;
                            return (
                              <>
                                <Link
                                  to="#"
                                  className="play_audio text-primary"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setPopupAudio({
                                      name: user.subscriberfullname || user.fullname || user.subscriber_name || (user.call_from ? `Call from ${user.call_from}` : "Subscriber Recording"),
                                      audioUrl: streamUrl,
                                    });
                                  }}
                                >
                                  <FontAwesomeIcon icon={faPlay} />
                                </Link>

                                <a
                                  href={downloadUrl}
                                  className="btn btn-sm"
                                  style={{ marginLeft: "10px" }}
                                  download={`recording_${user.auto_id}.mp3`}
                                  title="Download Recording"
                                >
                                  <FontAwesomeIcon
                                    icon={faDownload}
                                    className="text-primary"
                                  />
                                </a>
                              </>
                            );
                          })()
                        ) : (
                          "----"
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="11" style={{ textAlign: "center" }}>
                    No Records
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Audio Player Popup */}
      <AudioPlayerPopup
        isOpen={Boolean(popupAudio)}
        onClose={() => setPopupAudio(null)}
        name={popupAudio?.name}
        audioUrl={popupAudio?.audioUrl}
      />
    </div>
  );
};

export default IncomingCallReport;
